import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { createOrder, listOrdersByMerchant, listProducts } from "../services/api";
import { FulfillmentForm } from "../components/FulfillmentForm";
import { OrderList } from "../components/OrderList";
import {
  EmptyState,
  FilterBar,
  PageHeader,
  SearchField,
  matchesStatusFilter,
  orderMatchesSearch,
  statusFilterOptions,
} from "../components/orderKit";
import { orderLineDisplayLabel } from "../utils/orderLineMedia";
import { useMerchantStore } from "../store/useMerchantStore";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

function newestFirst(a, b) {
  return (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0);
}

export function Orders() {
  const { merchantId } = useMerchantStore();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  // `/orders?new=1` opens the New order panel directly (e.g. from a shortcut link).
  const [composerOpen, setComposerOpen] = useState(() => searchParams.get("new") === "1");
  const [notice, setNotice] = useState("");

  async function refresh() {
    setError("");
    if (!merchantId) {
      setOrders([]);
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const [os, prods] = await Promise.all([
        listOrdersByMerchant(merchantId),
        listProducts(merchantId),
      ]);
      setOrders([...(os || [])].sort(newestFirst));
      setProducts(prods);
    } catch (err) {
      setError(friendlyError(err, t("orders.load_failed")));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  useEffect(() => {
    if (!composerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setComposerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [composerOpen]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 8000);
    return () => clearTimeout(timer);
  }, [notice]);

  async function submit(payload) {
    const created = await createOrder(payload);
    setComposerOpen(false);
    setFilter("all");
    setNotice({ orderId: created?.id ?? null });
    await refresh();
  }

  const filterOptions = useMemo(() => statusFilterOptions(orders), [orders]);
  const visible = useMemo(
    () => orders.filter((o) => matchesStatusFilter(o, filter) && orderMatchesSearch(o, search, orderLineDisplayLabel)),
    [orders, filter, search]
  );

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={t("nav.orders")}
        title={t("nav.orders")}
        lead={merchantId ? t("orders.lead") : t("common.sign_in_again")}
        actions={
          <>
            <button type="button" onClick={() => refresh()} disabled={!merchantId || loading}>
              <i className={`fa-solid fa-rotate-right${loading ? " fa-spin" : ""}`} aria-hidden="true" /> {t("common.refresh")}
            </button>
            <button type="button" className="primary" onClick={() => setComposerOpen(true)} disabled={!merchantId}>
              <i className="fa-solid fa-plus" aria-hidden="true" /> {t("orders.new_order")}
            </button>
          </>
        }
      />

      {notice ? (
        <div className="alert alert--success" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          <span>
            <strong>
              {notice.orderId ? t("orders.created_with_id", { id: notice.orderId }) : t("orders.created")}
            </strong>{" "}
            {t("orders.pack_next")}
          </span>
          <Link
            className="button-link button-link--primary"
            to={notice.orderId ? `/fulfill?order=${notice.orderId}` : "/fulfill"}
          >
            <i className="fa-solid fa-clipboard-check" aria-hidden="true" /> {t("orders.fulfil_now")}
          </Link>
        </div>
      ) : null}
      {error ? (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      ) : null}

      <div className="list-toolbar">
        <FilterBar options={filterOptions} value={filter} onChange={setFilter} ariaLabel={t("orders.status_aria")} />
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t("orders.search_placeholder")}
          ariaLabel={t("orders.search_aria")}
        />
      </div>

      {loading && orders.length === 0 ? (
        <div className="loading">{t("orders.loading")}</div>
      ) : visible.length === 0 ? (
        <EmptyState
          title={orders.length === 0 ? t("orders.empty_title") : t("common.nothing_here")}
          message={orders.length === 0 ? t("orders.empty_message") : t("orders.no_match")}
          action={
            orders.length === 0 ? (
              <button type="button" className="primary" onClick={() => setComposerOpen(true)}>
                {t("orders.create_one")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setFilter("all");
                  setSearch("");
                }}
              >
                {t("orders.show_all")}
              </button>
            )
          }
        />
      ) : (
        <OrderList orders={visible} />
      )}

      {composerOpen ? (
        <div className="drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setComposerOpen(false)}>
          <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="new-order-title">
            <header className="drawer__header">
              <div>
                <div className="eyebrow">{t("orders.manual_order")}</div>
                <h2 id="new-order-title" style={{ margin: "4px 0 0" }}>{t("orders.new_order")}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setComposerOpen(false)}
                aria-label={t("common.close")}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </header>
            <FulfillmentForm
              merchantId={merchantId}
              products={products}
              onSubmit={submit}
              onCancel={() => setComposerOpen(false)}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
}
